// The POC's data set (bymoses/better-web-kanban index.html): 11 named tasks + 90 generated, every third with a
// cover (test/fixtures/img, 480 px wide), every 13th generated one blocked. Served by the mock api in the browser,
// so the app's look-and-feel tests run on the same board as the POC's.
import type { Snapshot, TaskStatus } from "../types";
import { board, type RawBoard } from "./board";

const NAMED: [string, string, TaskStatus, string, string, boolean?][] = [
	["tsk_4k2m9q1x0a", "Web UI: booklet layout", "doing", "tasks", "Michael"],
	["tsk_8d1r7c3n2b", "Pipeline: daisyUI option", "doing", "pipelines", "agent:tasks-web"],
	["tsk_2p6v0h5k3c", "R2 CORS for attachments", "todo", "tasks", "agent:tasks-web"],
	["tsk_9w3e1j8m4d", "Telegram curator design", "todo", "tasks", "Michael", true],
	["tsk_5t7y2u9i5e", "GitHub hook: Task trailer", "todo", "tasks", "N"],
	["tsk_1a8s4d6f6f", "Tokens for B and N", "todo", "tasks", "Michael"],
	["tsk_7g2h5j1k7g", "Backup retention policy", "review", "tasks", "B"],
	["tsk_3l9z6x2c8h", "Cloudflare adapter", "done", "tasks", "agent:tasks-runtime"],
	["tsk_6v4b8n3m9i", "D1 spike", "done", "tasks", "agent:tasks-d1-spike"],
	["tsk_0q5w9e7r0j", "Blob orphans sweep", "todo", "tasks", "B"],
	["tsk_2r8t4y6u1k", "Landing copy v2", "review", "eclion", "N"],
];
const BLOCKER = "tsk_1a8s4d6f6f";
export const DIGITERIUM = new Set(["digiterium", "eclion", "osmix"]);

/** Task.context of the first named task (Decision task-context): a person with a note, a project, a task. */
export const POC_CONTEXT: [string, { id: string; note: string | null }[]][] = [
	["tsk_4k2m9q1x0a", [{ id: "usr_michael", note: "decides" }, { id: "spc_pipelines", note: null }, { id: "tsk_8d1r7c3n2b", note: "same stack" }]],
];
/** Outside links of a task besides its cover (the first named task's call notes). */
export const POC_LINKS: Record<string, { url: string; kind: string; title: string | null; addedAt: string }[]> = {
	tsk_4k2m9q1x0a: [{ url: "https://t.me/c/1/2", kind: "tg", title: "Call notes", addedAt: "2026-10-18T09:00:00.000Z" }],
};

/** The POC's long body (twice), so the sheet has content past full. */
export function pocBody(title: string): string {
	const t = title.toLowerCase();
	const p = [
		`Goal: ${t} without breaking what already works. Agents and people read this task as the single source of truth, so keep it current.`,
		"Context: the booklet layout keeps every page in one horizontal scroll row with snap points. On a phone each page is full width, so swiping moves between columns the same way it moves between photos. Nothing slides in from the side; the URL decides which pages exist.",
		"The sheet is a second scroller stacked on top: a transparent spacer the height of the screen, then this panel. Peek, half and full are just snap targets. Past full, the panel keeps scrolling as plain content, which is what you are doing right now.",
		"Acceptance: columns snap one at a time; the sheet opens at peek; status buttons move the card; scrolling the sheet back down closes it and pops the URL; browser back does the same.",
		"Open points: detents closer than 30% get skipped on a fast flick; iOS toolbar resizing changes dvh mid-gesture; the graph canvas owns touch, so leaving it is by back only.",
		"Notes from the last run: snap-stop always prevents flying past a column on a strong swipe. overscroll-behavior contain keeps the page behind from bouncing.",
	];
	return [...p, ...p].join("\n\n");
}

export function pocFixture(): Snapshot {
	const rows = NAMED.map(([id, title, status, project, who, blocked]) => ({ id, title, status, project, who, blocked: !!blocked }));
	const verbs = ["Fix", "Add", "Review", "Draft", "Migrate", "Test", "Document", "Refactor", "Ship", "Measure"];
	const things = ["login flow", "event replay", "board query", "token rotation", "FTS ranking", "attachment upload", "cron backup", "graph layout", "CLI output", "workload view", "onboarding copy", "invoice export"];
	const who = ["Michael", "B", "N", "agent:tasks-web", "agent:curator"];
	const proj = ["tasks", "eclion", "pipelines", "osmix"];
	const weights: TaskStatus[] = ["todo", "todo", "todo", "doing", "doing", "review", "done", "done", "done", "dropped"];
	for (let i = 0; i < 90; i++)
		rows.push({
			id: `tsk_${((i * 2654435761) >>> 0).toString(32).padStart(10, "0").slice(-10)}`,
			title: `${verbs[i % 10]} ${things[(i * 7) % 12]}`,
			status: weights[(i * 3) % 10]!,
			project: proj[i % 4]!,
			who: who[(i * 5) % 5]!,
			blocked: i % 13 === 0,
		});
	const key = (s: string) => s.replace(/[^a-z0-9]/gi, "").toLowerCase();
	const persons = [...new Set(rows.map((r) => r.who))].map((name) => ({ id: `usr_${key(name)}`, name, kind: name.startsWith("agent:") ? ("agent" as const) : ("human" as const) }));
	// home merge: Eclion and Osmix live in the connected digiterium instance (address ids `digiterium/<id>`)
	const remote = (p: string) => (DIGITERIUM.has(p) ? "digiterium" : null);
	const addr = (p: string, id: string) => (remote(p) ? `${remote(p)}/${id}` : id);
	// space trees: organisations hold projects (deeper too); Michael is in Whyti's App, not Whyti: Whyti is a stub
	const space = (name: string, parent: string | null, stub = false, type: "org" | "team" | "project" | "personal" = "project") => ({
		id: addr(name, `spc_${key(name)}`),
		name: name[0]!.toUpperCase() + name.slice(1),
		status: "active" as const,
		repoIds: [],
		instance: remote(name),
		parent: parent && addr(name, `spc_${key(parent)}`),
		stub,
		type,
	});
	const projects = [
		space("personal", null, false, "personal"),
		space("studio404", null, false, "org"),
		space("tasks", "studio404"),
		space("web", "tasks"),
		space("pipelines", "studio404"),
		space("design", "studio404", false, "team"),
		space("whyti", null, true, "org"),
		space("app", "whyti"),
		space("digiterium", null, false, "org"),
		space("eclion", "digiterium"),
		space("osmix", "digiterium"),
	];
	const start = Date.UTC(2026, 9, 18, 12);
	const tasks: RawBoard["tasks"] = rows.map((r, i) => ({
		id: addr(r.project, r.id),
		instance: remote(r.project),
		title: r.title,
		status: r.status,
		projectId: addr(r.project, `spc_${key(r.project)}`),
		assigneeId: `usr_${key(r.who)}`,
		parentId: null,
		blockedBy: r.blocked ? [BLOCKER] : [],
		createdAt: new Date(start + i * 60_000).toISOString(), // unranked: oldest first = the POC's order
		updatedAt: new Date(start + i * 60_000).toISOString(),
		rank: null,
		cover: i % 3 === 0 ? `blob://mock/${((i / 3) % 12) + 1}.jpg` : null,
	}));
	return board({ tasks, projects, persons, repos: [] });
}
